import React, { useState } from "react";
import { apiCall } from "../../utils/ApiUtil";
import { toast } from "react-toastify";
import { FiUpload, FiX } from "../../assets/icons/vander";

const JobApplicationForm = ({ jobTitle, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: jobTitle || "",
    experience: "",
  });
  
  const [resumeFile, setResumeFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters long";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!formData.position.trim()) {
      newErrors.position = "Position is required";
    }
    
    if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = "Please enter a valid phone number";
    }
    
    if (!resumeFile) {
      newErrors.resume = "Resume file is required";
    } else {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(resumeFile.type)) {
        newErrors.resume = "Please upload a PDF or Word document";
      }
      if (resumeFile.size > 5 * 1024 * 1024) { // 5MB limit
        newErrors.resume = "File size should be less than 5MB";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        resume: "Please upload a PDF or Word document"
      }));
      return;
    }
    
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        resume: "File size should be less than 5MB"
      }));
      return;
    }
    
    setResumeFile(file);
    
    if (errors.resume) {
      setErrors(prev => ({
        ...prev,
        resume: ""
      }));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const removeFile = () => {
    setResumeFile(null);
    // Reset the file input
    const fileInput = document.getElementById('resume');
    if (fileInput) fileInput.value = '';
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(',')[1]; // Remove data:application/pdf;base64, prefix
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert file to base64
      const resumeData = await convertFileToBase64(resumeFile);
      
      const payload = {
        email: formData.email,
        action: "storeCareerApplication",
        data: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          position: formData.position,
          experience: formData.experience,
          resumeData: resumeData,
          resumeFileName: resumeFile.name
        }
      };

      const response = await apiCall(payload);
      
      if (response?.status === 200 && (response?.data?.statusCode === 201 || response?.data?.statusCode === 200)) {
        toast.success("Application submitted successfully! We'll get back to you soon.");
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } else {
        const errorMessage = response?.data?.body ? JSON.parse(response.data.body).message : "Something went wrong. Please try again.";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || "Server error occurred. Please try again.";
        toast.error(errorMessage);
      } else if (error.request) {
        // Network error
        toast.error("Network error. Please check your connection and try again.");
      } else {
        // Other errors
        toast.error("Failed to submit application. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="job-application-modal">
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">Apply for {jobTitle || "Position"}</h3>
          <button type="button" className="modal-close" onClick={onClose}>
            <FiX />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="application-form">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="name" className="form-label">
                Full Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
              />
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>
            
            <div className="col-md-6 mb-3">
              <label htmlFor="email" className="form-label">
                Email Address <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
              />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>
          </div>
          
          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="phone" className="form-label">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Enter your phone number"
              />
              {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
            </div>
            
            <div className="col-md-6 mb-3">
              <label htmlFor="position" className="form-label">
                Position <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="position"
                name="position"
                className={`form-control ${errors.position ? 'is-invalid' : ''}`}
                value={formData.position}
                onChange={handleInputChange}
                placeholder="Enter position title"
              />
              {errors.position && <div className="invalid-feedback">{errors.position}</div>}
            </div>
          </div>
          
          <div className="mb-3">
            <label htmlFor="experience" className="form-label">
              Years of Experience
            </label>
            <select
              id="experience"
              name="experience"
              className="form-control"
              value={formData.experience}
              onChange={handleInputChange}
            >
              <option value="">Select experience level</option>
              <option value="0-1 years">0-1 years</option>
              <option value="1-2 years">1-2 years</option>
              <option value="2-3 years">2-3 years</option>
              <option value="3-5 years">3-5 years</option>
              <option value="5-7 years">5-7 years</option>
              <option value="7+ years">7+ years</option>
            </select>
          </div>
          
          <div className="mb-4">
            <label htmlFor="resume" className="form-label">
              Resume/CV <span className="text-danger">*</span>
            </label>
            <div className="file-upload-container">
                             {!resumeFile ? (
                 <div 
                   className="file-upload-area"
                   onDragOver={handleDragOver}
                   onDragLeave={handleDragLeave}
                   onDrop={handleDrop}
                 >
                   <input
                     type="file"
                     id="resume"
                     name="resume"
                     className={`file-input ${errors.resume ? 'is-invalid' : ''}`}
                     accept=".pdf,.doc,.docx"
                     onChange={handleFileChange}
                   />
                   <div className="upload-placeholder">
                     <FiUpload className="upload-icon" />
                     <p>Click to upload or drag and drop</p>
                     <small>PDF, DOC, or DOCX (Max 5MB)</small>
                   </div>
                 </div>
              ) : (
                <div className="file-preview">
                  <div className="file-info">
                    <FiUpload className="file-icon" />
                    <span className="file-name">{resumeFile.name}</span>
                    <span className="file-size">({(resumeFile.size / 1024 / 1024).toFixed(2)} MB)</span>
                  </div>
                  <button
                    type="button"
                    className="remove-file-btn"
                    onClick={removeFile}
                  >
                    <FiX />
                  </button>
                </div>
              )}
              {errors.resume && <div className="invalid-feedback d-block">{errors.resume}</div>}
            </div>
          </div>
          
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Application"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobApplicationForm; 